import apiClient from './client'

export interface AdminNotification {
  id: string
  title: string
  message: string
  type: 'SYSTEM' | 'EVENT' | 'PAYMENT' | 'USER' | 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS'
  status: 'UNREAD' | 'READ'
  isRead?: boolean
  userId?: string
  eventId?: string
  actionUrl?: string
  actionText?: string
  createdAt: string
  updatedAt: string
}

export interface UserNotificationItem {
  id: string
  title: string
  message: string
  type: string
  priority: string
  isGlobal: boolean
  isRead: boolean
  status: 'UNREAD' | 'READ'
  readStateSource?: 'SERVER' | 'CLIENT'
  actionUrl?: string | null
  actionText?: string | null
  expiresAt?: string | null
  createdAt: string
  updatedAt: string
  isReadDisplay?: boolean
}

export interface NotificationPagination {
  page: number
  limit: number
  total: number
  pages: number
}

export interface CreateNotificationDto {
  title: string
  message: string
  type: 'SYSTEM' | 'EVENT' | 'PAYMENT' | 'USER'
  userId?: string
  eventId?: string
}

export interface UpdateNotificationDto {
  title?: string
  message?: string
  type?: 'SYSTEM' | 'EVENT' | 'PAYMENT' | 'USER'
  status?: 'UNREAD' | 'READ'
}

export interface NotificationQueryDto {
  page?: number
  limit?: number
  type?: string
  status?: string
  userId?: string
  eventId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface UserNotificationQueryDto {
  page?: number
  limit?: number
  type?: string
  isRead?: boolean
}

export interface UserUnreadCountResponse {
  unreadCountServer: number
  unreadCount: number
}

export interface MarkUserNotificationAsReadResponse {
  notification: UserNotificationItem
  updatedServer: boolean
  requiresClientReadState: boolean
}

export interface MarkAllMyNotificationsAsReadResponse {
  updatedServerCount: number
  skippedGlobal: boolean
}

function unwrapApiData<T>(raw: any): T {
  return (raw?.data ?? raw) as T
}

function normalizePagination(raw: any, fallbackLimit: number, fallbackTotal = 0): NotificationPagination {
  return raw?.pagination
    || raw?.data?.pagination
    || raw?.data?.data?.pagination
    || raw?.meta?.pagination
    || raw?.data?.meta?.pagination
    || {
    page: 1,
    limit: fallbackLimit,
    total: fallbackTotal,
    pages: Math.max(1, Math.ceil(fallbackTotal / Math.max(fallbackLimit, 1))),
  }
}

function normalizeAdminNotification(item: any): AdminNotification {
  const status = item?.status === 'READ' || item?.status === 'UNREAD'
    ? item.status
    : item?.isRead
      ? 'READ'
      : 'UNREAD'

  return {
    ...item,
    status,
    isRead: typeof item?.isRead === 'boolean' ? item.isRead : status === 'READ',
  } as AdminNotification
}

export const notificationsApi = {
  // Admin Notifications
  async getNotifications(query?: NotificationQueryDto): Promise<{ notifications: AdminNotification[]; pagination: NotificationPagination }> {
    const response = await apiClient.get('/admin/notifications', { params: query })
    const raw = response.data

    const containers = [raw, raw?.data, raw?.data?.data, raw?.result, raw?.payload, raw?.data?.result]
    let notificationsSource: any[] = []

    for (const container of containers) {
      if (Array.isArray(container)) {
        notificationsSource = container
        break
      }

      if (Array.isArray(container?.notifications)) {
        notificationsSource = container.notifications
        break
      }

      if (Array.isArray(container?.items)) {
        notificationsSource = container.items
        break
      }

      if (Array.isArray(container?.rows)) {
        notificationsSource = container.rows
        break
      }
    }

    const notifications = notificationsSource.map((item) => normalizeAdminNotification(item))

    return {
      notifications,
      pagination: normalizePagination(raw, query?.limit ?? 20, notifications.length),
    }
  },

  async getNotificationById(id: string): Promise<AdminNotification> {
    const response = await apiClient.get(`/admin/notifications/${id}`)
    return unwrapApiData<AdminNotification>(response.data)
  },

  async createNotification(data: CreateNotificationDto): Promise<AdminNotification> {
    const response = await apiClient.post('/admin/notifications', data)
    return unwrapApiData<AdminNotification>(response.data)
  },

  async updateNotification(id: string, data: UpdateNotificationDto): Promise<AdminNotification> {
    const response = await apiClient.put(`/admin/notifications/${id}`, data)
    return unwrapApiData<AdminNotification>(response.data)
  },

  async deleteNotification(id: string): Promise<void> {
    await apiClient.delete(`/admin/notifications/${id}`)
  },

  async getMyNotifications(query?: UserNotificationQueryDto): Promise<{ data: UserNotificationItem[]; pagination: NotificationPagination }> {
    const response = await apiClient.get('/notifications/me', { params: query })
    const raw = response.data
    const data = Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.data?.data)
        ? raw.data.data
        : Array.isArray(raw?.notifications)
          ? raw.notifications
          : Array.isArray(raw?.data?.notifications)
            ? raw.data.notifications
      : []

    return {
      data,
      pagination: normalizePagination(raw, query?.limit ?? 20, data.length),
    }
  },

  async getMyUnreadCount(): Promise<UserUnreadCountResponse> {
    const response = await apiClient.get('/notifications/me/unread-count')
    return unwrapApiData<UserUnreadCountResponse>(response.data)
  },

  async markUserNotificationAsRead(id: string): Promise<MarkUserNotificationAsReadResponse> {
    const response = await apiClient.patch(`/notifications/me/${id}/read`)
    return unwrapApiData<MarkUserNotificationAsReadResponse>(response.data)
  },

  async markAllMyNotificationsAsRead(): Promise<MarkAllMyNotificationsAsReadResponse> {
    const response = await apiClient.patch('/notifications/me/read-all')
    return unwrapApiData<MarkAllMyNotificationsAsReadResponse>(response.data)
  },
}
